import { isNativePlatform } from "@lib/platform";
import { getPlatformStorage } from "@lib/platform/storage";
import { getSupabaseAuthStorageKey } from "@lib/supabase/auth-storage";
import { isPasswordRecoveryPending } from "@lib/auth/password-recovery";
import { readAuthRedirectParams } from "@lib/auth/auth-redirect-params";
import { getWelcomeSeen, markWelcomeSeen } from "@features/welcome/welcome-storage";

/** Reads the persisted Supabase session straight from platform storage. */
async function hasPersistedSession(): Promise<boolean> {
  try {
    const raw = await getPlatformStorage().get(getSupabaseAuthStorageKey());
    if (!raw) return false;

    const parsed = JSON.parse(raw) as
      | { user?: { id?: string } }
      | { currentSession?: { user?: { id?: string } } | null };

    if ("user" in parsed && parsed.user?.id) return true;
    const nested = "currentSession" in parsed ? parsed.currentSession : null;
    return !!nested?.user?.id;
  } catch {
    return false;
  }
}

async function computeDecision(): Promise<boolean> {
  // Native-only first-run intro.
  if (!isNativePlatform()) return false;

  if (await getWelcomeSeen()) return false;

  // Never cover password-recovery or email/OAuth deep-link flows. Treat as seen
  // so a later sign-out cannot resurrect the intro.
  if (isPasswordRecoveryPending() || readAuthRedirectParams().code) {
    await markWelcomeSeen();
    return false;
  }

  // An existing session means this is not a fresh, pre-login install.
  if (await hasPersistedSession()) {
    await markWelcomeSeen();
    return false;
  }

  return true;
}

let cachedDecision: Promise<boolean> | null = null;

/**
 * Latched decision for this app launch: computed once from a settled snapshot
 * (never from live `userInfo`, which toggles) and memoized so the splash screen
 * and the gate agree. Answers "should the first-run welcome be shown?".
 */
export function resolveWelcome(): Promise<boolean> {
  if (!cachedDecision) cachedDecision = computeDecision();
  return cachedDecision;
}
